import React, { useMemo, useState } from 'react';
import { Button, Skeleton, Empty, Select, Modal, Upload, message } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import PageContainer from '@/components/PageContainer';
import { useStationsOverview } from '@/api/stationsOverview';
import { useUploadMonthlyPlan } from '@/api/monthlyPlan';
import type { StationOverviewItem } from '@/types/api';

const PLAN_COLOR = '#2563eb';
const ACTUAL_COLOR = '#0d9488';
const GRID_COLOR = '#f0f2f5';
const MUTED = '#6b7280';

function buildYearOptions(): number[] {
  const current = dayjs().year();
  const years: number[] = [];
  for (let y = current; y >= 2024; y--) years.push(y);
  return years;
}

// ── aggregated bar chart ──
function barChartOpt(
  months: number[],
  planned: number[],
  actual: number[],
  unitLabel: string,
): EChartsOption {
  const labels = months.map((m) => `${m}月`);
  return {
    tooltip: {
      trigger: 'axis' as const,
      formatter: (params: unknown) => {
        const items = params as { name: string; seriesName: string; value: number }[];
        let html = items[0]?.name || '';
        for (const item of items) {
          html += `<br/>${item.seriesName}: ${item.value.toLocaleString()} ${unitLabel}`;
        }
        return html;
      },
    },
    legend: { data: ['计划', '实际'], bottom: 0, textStyle: { fontSize: 11, color: MUTED }, itemWidth: 14, itemHeight: 8 },
    grid: { left: 60, right: 16, top: 16, bottom: 36 },
    xAxis: { type: 'category' as const, data: labels, axisLabel: { fontSize: 10, color: '#888' }, axisLine: { lineStyle: { color: '#E2E8F0' } } },
    yAxis: { type: 'value' as const, name: unitLabel, nameTextStyle: { fontSize: 10, color: '#888' }, axisLabel: { fontSize: 10, color: '#888' }, splitLine: { lineStyle: { color: GRID_COLOR } } },
    series: [
      { name: '计划', type: 'bar' as const, data: planned, itemStyle: { color: PLAN_COLOR, borderRadius: [4, 4, 0, 0] }, barGap: '20%' },
      { name: '实际', type: 'bar' as const, data: actual, itemStyle: { color: ACTUAL_COLOR, borderRadius: [4, 4, 0, 0] } },
    ],
  };
}

// ── aggregate stations ──
function aggregateMonthly(all: StationOverviewItem[], sel: string) {
  if (sel === '__total__') {
    const map: Record<number, { plannedGen: number; actualGen: number; plannedRev: number; actualRev: number }> = {};
    for (const s of all) {
      for (const m of s.monthly) {
        const e = map[m.month] || (map[m.month] = { plannedGen: 0, actualGen: 0, plannedRev: 0, actualRev: 0 });
        e.plannedGen += m.planned_gen_mwh;
        e.actualGen += m.actual_gen_mwh;
        e.plannedRev += m.planned_revenue_yuan;
        e.actualRev += m.actual_revenue_yuan;
      }
    }
    const months = Object.keys(map).map(Number).sort((a, b) => a - b);
    return {
      months,
      plannedGen: months.map((m) => map[m].plannedGen),
      actualGen: months.map((m) => map[m].actualGen),
      plannedRev: months.map((m) => map[m].plannedRev),
      actualRev: months.map((m) => map[m].actualRev),
      info: null as StationOverviewItem | null,
    };
  }
  const s = all.find((x) => x.name === sel);
  if (!s) return null;
  const months = s.monthly.map((m) => m.month).sort((a, b) => a - b);
  const byMonth = new Map(s.monthly.map((m) => [m.month, m]));
  return {
    months,
    plannedGen: months.map((m) => byMonth.get(m)!.planned_gen_mwh),
    actualGen: months.map((m) => byMonth.get(m)!.actual_gen_mwh),
    plannedRev: months.map((m) => byMonth.get(m)!.planned_revenue_yuan),
    actualRev: months.map((m) => byMonth.get(m)!.actual_revenue_yuan),
    info: s,
  };
}

// ── page ──
const StationsOverviewPage: React.FC = () => {
  const currentYear = dayjs().year();
  const [year, setYear] = useState(currentYear);
  const [station, setStation] = useState('__total__');
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data, isLoading, error, refetch } = useStationsOverview(year);
  const uploadMutation = useUploadMonthlyPlan();
  const yearOptions = buildYearOptions();

  const allStations: StationOverviewItem[] = data?.data?.stations ?? [];
  const stationOptions = useMemo(() => {
    const opts = [{ label: '合计', value: '__total__' }];
    for (const s of allStations) opts.push({ label: s.name, value: s.name });
    return opts;
  }, [allStations]);

  const agg = useMemo(() => aggregateMonthly(allStations, station), [allStations, station]);

  const genOpt = useMemo(() => {
    if (!agg) return null;
    return barChartOpt(agg.months, agg.plannedGen, agg.actualGen, 'MWh');
  }, [agg]);

  const revOpt = useMemo(() => {
    if (!agg) return null;
    return barChartOpt(agg.months, agg.plannedRev, agg.actualRev, '元');
  }, [agg]);

  const uploadProps: UploadProps = {
    accept: '.xlsx',
    maxCount: 1,
    beforeUpload: (file) => {
      uploadMutation.mutate(file, {
        onSuccess: (res: any) => {
          const inner = res?.data as any;
          const imported = inner?.imported ?? inner?.row_count ?? 0;
          const errs = (inner?.errors as string[]) || [];
          if (errs.length > 0) {
            message.warning(`导入完成：${imported} 条成功，${errs.length} 条失败。${errs.slice(0, 3).join('；')}`);
          } else {
            message.success(`导入成功：${imported} 条记录`);
          }
          setUploadOpen(false);
          refetch();
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || '请检查文件格式';
          message.error(`导入失败：${msg}`);
        },
      });
      return false;
    },
  };

  // ── loading ──
  if (isLoading) {
    return (
      <PageContainer title="场站概览">
        <div style={{ background: '#fff', borderRadius: 12, padding: 40, border: '1px solid #e5e7eb' }}>
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title="场站概览">
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e5e7eb' }}>
          <Empty description={`加载失败: ${(error as Error).message}`} />
        </div>
      </PageContainer>
    );
  }

  if (allStations.length === 0) {
    return (
      <PageContainer title="场站概览">
        <div style={{ background: '#fff', borderRadius: 12, padding: 40, border: '1px solid #e5e7eb' }}>
          <Empty description="暂无场站数据" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="场站概览">
      {/* toolbar */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: MUTED }}>年份:</span>
        <Select value={year} onChange={(v) => setYear(v)} style={{ width: 100 }} options={yearOptions.map((y) => ({ label: `${y}年`, value: y }))} />
        <span style={{ fontSize: 13, color: MUTED, marginLeft: 8 }}>场站:</span>
        <Select value={station} onChange={(v) => setStation(v)} style={{ width: 160 }} options={stationOptions} />
        {station !== '__total__' && (
          <Button size="small" icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>导入计划</Button>
        )}
      </div>

      {/* station info (only when single station selected) */}
      {agg?.info && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '12px 20px', marginBottom: 16, display: 'flex', gap: 24, fontSize: 13, color: MUTED, flexWrap: 'wrap' }}>
          <span>装机: <b style={{ color: '#1a2b4a' }}>{agg.info.pv_capacity}MW</b></span>
          <span>配储: <b style={{ color: '#1a2b4a' }}>{agg.info.bat_p}MW / {agg.info.bat_c}MWh</b></span>
          <span>机制电价: <b style={{ color: '#1a2b4a' }}>{agg.info.subsidy_price}</b></span>
          <span>并网: <b style={{ color: '#1a2b4a' }}>{agg.info.grid_connected_date || '--'}</b></span>
        </div>
      )}

      {/* charts */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '20px 24px' }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#374151', marginBottom: 12 }}>
          月度发电量对比 (MWh)
        </div>
        {genOpt && <ReactECharts option={genOpt} style={{ height: 300 }} notMerge />}

        <div style={{ fontWeight: 600, fontSize: 14, color: '#374151', margin: '24px 0 12px' }}>
          月度电费收入对比 (元)
        </div>
        {revOpt && <ReactECharts option={revOpt} style={{ height: 300 }} notMerge />}
      </div>

      {/* upload modal */}
      <Modal title={`导入计划 - ${station}`} open={uploadOpen} onCancel={() => setUploadOpen(false)} footer={null} destroyOnClose>
        <div style={{ marginBottom: 16 }}>
          <a href="/api/v1/monthly-plan/template" download>
            <Button icon={<DownloadOutlined />}>下载模板</Button>
          </a>
          <span style={{ fontSize: 12, color: MUTED, marginLeft: 12 }}>
            模板：场站名称 | 年份 | 月份 | 计划发电量(MWh) | 计划电费收入(元)
          </span>
        </div>
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon"><UploadOutlined style={{ fontSize: 24, color: '#0d9488' }} /></p>
          <p style={{ fontSize: 13 }}>点击或拖拽 Excel 文件上传</p>
          <p style={{ fontSize: 11, color: MUTED }}>仅支持 .xlsx 格式</p>
        </Upload.Dragger>
        {uploadMutation.isPending && <div style={{ textAlign: 'center', marginTop: 12, color: MUTED }}>正在上传...</div>}
      </Modal>
    </PageContainer>
  );
};

export default StationsOverviewPage;
